/**
 * `<NotesEmptyState>` — empty-state for the Notes tab (per-(candidate × JD)).
 *
 * Phase 2.5 update per candidate-detail-rebuild-spec.md §6.6:
 *   - Copy is per-pair (candidateName + jdTitle).
 *   - No "Notes load with the tab" narration (H13 — deleted).
 *   - No CTA button — the composer above IS the CTA (HoD §3.2 ruling).
 *
 * Spec: candidate-detail-rebuild-spec.md §6.6.
 */
import type { JSX } from "react";
import { NotebookPen } from "lucide-react";

interface NotesEmptyStateProps {
  /** Candidate first name or full name. */
  candidateName?: string;
  /** JD title for the current context. */
  jdTitle?: string;
}

export function NotesEmptyState({
  candidateName,
  jdTitle,
}: NotesEmptyStateProps): JSX.Element {
  const firstName = candidateName
    ? candidateName.split(" ")[0] ?? candidateName
    : "this candidate";

  const body =
    jdTitle
      ? `Use notes for what's not in the resume — references, interview observations, recruiter context for ${firstName} on ${jdTitle}.`
      : `Your team's notes for ${firstName} will show up here, newest first.`;

  return (
    <div
      className="flex flex-1 flex-col items-center justify-center gap-4 py-6"
      data-testid="notes-empty"
    >
      <NotebookPen size={48} className="text-text-tertiary" aria-hidden="true" />
      <div className="flex flex-col items-center gap-1.5">
        <h3 className="text-lg font-semibold text-text-primary">No notes yet</h3>
        <p className="max-w-md text-center text-sm text-text-secondary">{body}</p>
      </div>
    </div>
  );
}
